import { createContext, type ReactNode, useContext, useMemo, useRef, useState } from 'react';

export interface TableFocus {
  focusedRowKey: string;
  focusedColumnKey: string;
  childFocusStrategy: ChildFocusStrategy;
  setFocus: (rowKey: string, columnKey: string, childFocusStrategy?: ChildFocusStrategy) => void;
}

export type ChildFocusStrategy = 'first' | 'last';

const TableFocusContext = createContext<TableFocus | null>(null);

export function TableFocusProvider({ children }: { children: ReactNode }) {
  // When table focus is updated, we want to trigger a re-render,
  // but we also want immediate access to the new values.
  // Hence why they are stored as both a ref and a state.
  const focusedRowKeyRef = useRef<string>('');
  const [_focusedRowKey, setFocusedRowKey] = useState<string>('');
  const focusedColumnKeyRef = useRef<string>('');
  const [_focusedColumnKey, setFocusedColumnKey] = useState<string>('');
  const childFocusStrategyRef = useRef<ChildFocusStrategy>('first');
  const [_childFocusStrategy, setChildFocusStrategy] = useState<ChildFocusStrategy>('first');

  const tableFocus: TableFocus = useMemo(
    () => ({
      get focusedRowKey() {
        return focusedRowKeyRef.current;
      },
      get focusedColumnKey() {
        return focusedColumnKeyRef.current;
      },
      get childFocusStrategy() {
        return childFocusStrategyRef.current;
      },
      setFocus: (rowKey, columnKey, childFocusStrategy) => {
        focusedRowKeyRef.current = rowKey;
        setFocusedRowKey(rowKey);

        focusedColumnKeyRef.current = columnKey;
        setFocusedColumnKey(columnKey);

        if (childFocusStrategy) {
          childFocusStrategyRef.current = childFocusStrategy;
          setChildFocusStrategy(childFocusStrategy);
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_focusedRowKey, _focusedColumnKey, _childFocusStrategy],
  );

  return <TableFocusContext.Provider value={tableFocus}>{children}</TableFocusContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTableFocus(): TableFocus {
  const tableFocus = useContext(TableFocusContext);
  if (!tableFocus) {
    throw new Error('Cannot use table focus outside of a table');
  }

  return tableFocus;
}
